import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { ExpandMore, Close, Edit, Check, Warning } from '@mui/icons-material';
import Navigation from '../Shared/Navigation.tsx';
import { useTranslation } from '@/core/i18n/i18n';
import { Focus, Objective, ObjectiveBatchSaveInput } from '@shared/schema';
import PlanAnualIcon from '@/static/icons/Plan-anual.svg?url';
import NewObjective from './Components/NewObjective.tsx';
import { useNavigate } from 'react-router-dom';
import { useAnyLoading } from '@/core/useAnyLoading';

interface DefineObjectivesProps {
  focuses?: Focus[];
  objectives?: Objective[];
  currentVersion: number | null;
  externalData?: any;
  isLoadingFocuses?: boolean;
  isLoadingObjectives?: boolean;
  errorMessage?: string | null;
  readOnlyMode: boolean | null;
  selectedYear: string | number | null;
  currentBusinessUnit?: string | null;
  fetchAnnualPlanStep1Context: (versionId: number) => Promise<unknown>;
  saveObjectivesBatch: (versionId: number, data: ObjectiveBatchSaveInput) => Promise<unknown>;
  scopeName: string;
}

const MAX_OBJECTIVE_LENGTH = 255;

type ObjectiveWithMeta = Objective & {
  originalName: string;
  isNew?: boolean;
  isDirty?: boolean;
};

const DefineObjectives: React.FC<DefineObjectivesProps> = ({
  focuses,
  objectives,
  fetchAnnualPlanStep1Context,
  saveObjectivesBatch,
  selectedYear,
  currentVersion,
  readOnlyMode,
  currentBusinessUnit,
  scopeName,
}) => {
  const isAnyLoading = useAnyLoading();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const versionId = currentVersion;
    if (versionId) {
      fetchAnnualPlanStep1Context(versionId);
    }
  }, [currentVersion, fetchAnnualPlanStep1Context]);

  const [objectivesByFocus, setObjectivesByFocus] = useState<Record<number, ObjectiveWithMeta[]>>(
    {},
  );
  const [expandedPanel, setExpandedPanel] = useState<number | false>(false);
  const [loadedFocuses, setLoadedFocuses] = useState<Focus[]>([]);
  const [deletedObjectiveIds, setDeletedObjectiveIds] = useState<Set<number>>(new Set());
  const [objectivePendingDeletion, setObjectivePendingDeletion] =
    useState<ObjectiveWithMeta | null>(null);
  const [editingObjective, setEditingObjective] = useState<{ id: number; focusId: number } | null>(
    null,
  );
  const [editingValue, setEditingValue] = useState('');
  const [editingError, setEditingError] = useState<string | null>(null);
  const editingInputRef = React.useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setLoadedFocuses(focuses ?? []);
  }, [focuses]);

  useEffect(() => {
    if (editingObjective && editingInputRef.current) {
      editingInputRef.current.focus();
      editingInputRef.current.select();
    }
  }, [editingObjective]);

  useEffect(() => {
    setObjectivesByFocus((prev) => {
      const nextState: Record<number, ObjectiveWithMeta[]> = {};

      (focuses ?? []).forEach((focus) => {
        nextState[focus.APFocusId] = [];
      });

      (objectives ?? []).forEach((objective) => {
        if (!nextState[objective.APFocusId]) {
          nextState[objective.APFocusId] = [];
        }
        const previous = prev[objective.APFocusId]?.find(
          (item) => item.APObjectiveId === objective.APObjectiveId,
        );
        nextState[objective.APFocusId]!.push({
          ...objective,
          originalName: previous?.originalName ?? objective.Name,
          isNew: false,
          isDirty: false,
        });
      });

      Object.entries(prev).forEach(([focusId, list]) => {
        const numericFocusId = Number(focusId);
        if (!nextState[numericFocusId]) {
          nextState[numericFocusId] = [];
        }
        list
          .filter((item) => item.isNew)
          .forEach((item) => {
            nextState[numericFocusId]!.push(item);
          });
      });

      return nextState;
    });
    setDeletedObjectiveIds((prev) => {
      const updated = new Set(prev);
      (objectives ?? []).forEach((objective) => {
        if (updated.has(objective.APObjectiveId)) {
          updated.delete(objective.APObjectiveId);
        }
      });
      return updated;
    });
  }, [objectives, focuses]);

  const handleAddObjective = (focusId: number, newObjective: string) => {
    const trimmed = newObjective.trim();
    if (!trimmed) return;
    const tempId = -Date.now();
    const nowIso = new Date().toISOString();
    setObjectivesByFocus((prev) => {
      const next = { ...prev };
      const currentList = [...(next[focusId] || [])];
      currentList.push({
        APObjectiveId: tempId,
        APFocusId: focusId,
        APVersionId: currentVersion ?? 0,
        Name: trimmed,
        IsDeleted: false,
        Created_at: nowIso,
        Updated_at: nowIso,
        ActivityCount: 0,
        originalName: trimmed,
        isNew: true,
        isDirty: false,
      });
      next[focusId] = currentList;
      return next;
    });
  };

  const handleStartEditObjective = (focusId: number, objectiveId: number) => {
    const objective = objectivesByFocus[focusId]?.find((obj) => obj.APObjectiveId === objectiveId);
    if (!objective) return;
    setEditingObjective({ focusId, id: objectiveId });
    setEditingValue(objective.Name);
    setEditingError(null);
  };

  const handleCancelEditObjective = () => {
    setEditingObjective(null);
    setEditingValue('');
    setEditingError(null);
  };

  const handleConfirmEditObjective = () => {
    if (!editingObjective) return;
    if (editingError) return;
    const trimmed = editingValue.trim();
    if (!trimmed) {
      setEditingError(t('defineObjectives.objectiveRequired'));
      return;
    }
    if (trimmed.length > MAX_OBJECTIVE_LENGTH) {
      setEditingError(
        t('defineObjectives.maxLengthExceeded', { max: MAX_OBJECTIVE_LENGTH.toString() }),
      );
      return;
    }
    setObjectivesByFocus((prev) => {
      const next = { ...prev };
      const list = [...(next[editingObjective.focusId] || [])];
      const index = list.findIndex((obj) => obj.APObjectiveId === editingObjective.id);
      if (index >= 0) {
        const target = list[index];
        if (!target) return next;
        const isChanged = !target.isNew && target.originalName !== trimmed;
        list[index] = {
          ...target,
          Name: trimmed,
          isDirty: target.isNew ? false : isChanged,
          originalName: target.isNew ? trimmed : target.originalName,
        };
        next[editingObjective.focusId] = list;
      }
      return next;
    });
    setEditingObjective(null);
    setEditingValue('');
    setEditingError(null);
  };

  const handleEditingValueChange = (value: string) => {
    if (value.length > MAX_OBJECTIVE_LENGTH) {
      setEditingError(
        t('defineObjectives.maxLengthExceeded', { max: MAX_OBJECTIVE_LENGTH.toString() }),
      );
      return;
    }
    setEditingValue(value);
    if (!value.trim()) {
      setEditingError(t('defineObjectives.objectiveRequired'));
    } else {
      setEditingError(null);
    }
  };

  const handleRequestDeleteObjective = (objective: ObjectiveWithMeta) => {
    setObjectivePendingDeletion(objective);
  };

  const handleCloseDeleteDialog = () => {
    setObjectivePendingDeletion(null);
  };

  const handleConfirmDeleteObjective = () => {
    if (!objectivePendingDeletion) return;
    const { APObjectiveId, APFocusId, isNew } = objectivePendingDeletion;
    setObjectivesByFocus((prev) => {
      const next = { ...prev };
      next[APFocusId] = (next[APFocusId] || []).filter(
        (obj) => obj.APObjectiveId !== APObjectiveId,
      );
      return next;
    });
    if (!isNew && APObjectiveId > 0) {
      setDeletedObjectiveIds((prev) => {
        const updated = new Set(prev);
        updated.add(APObjectiveId);
        return updated;
      });
    }
    setObjectivePendingDeletion(null);
  };

  const deleteActivitiesMessage = React.useMemo(() => {
    if (!objectivePendingDeletion) return '';
    const count = objectivePendingDeletion.ActivityCount ?? 0;
    if (count === 0) {
      return t('defineObjectives.deleteDialogNoActivities');
    }
    if (count === 1) {
      return t('defineObjectives.deleteDialogActivitiesSingular', {
        count: count.toString(),
      });
    }
    return t('defineObjectives.deleteDialogActivitiesPlural', {
      count: count.toString(),
    });
  }, [objectivePendingDeletion, t]);

  const hasFocusesWithoutObjectives = loadedFocuses.some(
    (focus) => (objectivesByFocus[focus.APFocusId] ?? []).length === 0,
  );
  const disableNext = hasFocusesWithoutObjectives && !readOnlyMode;
  const disableNextReason = disableNext ? t('defineObjectives.disableNextReason') : undefined;

  const handleExit = () => {
    navigate('/');
  };

  const persistObjectives = async (onSuccess: () => void) => {
    if (!currentVersion) return;
    if (editingObjective) {
      setEditingError(t('defineObjectives.finishEditingBeforeContinue'));
      return;
    }
    const objectivesList = Object.values(objectivesByFocus).flat();
    try {
      await saveObjectivesBatch(currentVersion, {
        create: objectivesList
          .filter((objective) => objective.isNew)
          .map((objective) => ({
            APFocusId: objective.APFocusId,
            Name: objective.Name.trim(),
          })),
        update: objectivesList
          .filter(
            (objective) => objective.isDirty && !objective.isNew && objective.APObjectiveId > 0,
          )
          .map((objective) => ({
            APObjectiveId: objective.APObjectiveId,
            Name: objective.Name.trim(),
          })),
        delete: Array.from(deletedObjectiveIds).filter((id) => id > 0),
      });
      setDeletedObjectiveIds(new Set());
      onSuccess();
    } catch (error) {
      console.error('Error saving objectives:', error);
    }
  };

  const handleAccordionChange =
    (panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  const handleSaveProgress = async () => {
    await persistObjectives(() => navigate('/'));
  };

  const handleBack = () => {
    navigate('/new-annual-plan');
  };

  const handleContinue = async () => {
    if (readOnlyMode) {
      navigate('/define-activities');
      return;
    }
    await persistObjectives(() => navigate('/define-activities'));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef6f6', pt: 4 }}>
      {/* Content */}
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1366px', mx: 'auto' }}>
        {/* Breadcrumb */}

        {/* Main Card */}
        <Paper sx={{ borderRadius: 1, boxShadow: 2, overflow: 'hidden' }}>
          {/* Header */}
          <Grid
            container
            sx={{
              pt: 2,
              px: { xs: 2, md: 3 },
              bgcolor: '#fafafa',
              borderBottom: '1px solid #A5B2BB',
              flexWrap: 'wrap',
              gap: { xs: 2, md: 0 },
            }}
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Grid container spacing={2} sx={{ mb: { xs: 1, md: 2 } }} flexWrap="wrap">
                {/* Bloque Plan Anual */}
                <Grid
                  size={{ xs: 12, md: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('common.annualPlan')}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {selectedYear}
                  </Typography>
                </Grid>

                {/* Bloque Ámbito */}
                <Grid
                  size={{ xs: 12, md: 'auto' }}
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('newAnnualPlan.scope')} {scopeName}
                    {currentBusinessUnit
                      ? ` · ${t('newAnnualPlan.businessUnit')}: ${currentBusinessUnit}`
                      : ''}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {t('newAnnualPlan.annualPlanDefinition')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 'auto' }}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: { xs: 'auto', md: 0 },
                }}
              >
                <img src={PlanAnualIcon} alt="Plan Anual" style={{ width: 70, height: 70 }} />
              </Box>
            </Grid>
          </Grid>

          {/* Stepper */}
          <Box sx={{ p: 3, bgcolor: 'white' }}>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#00BCE4',
                    color: 'white',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: 'white',
                          color: '#00BCE4',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">1</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step1')}
                      </Typography>
                      <Typography variant="caption">{t('common.step1Ttitle')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#f5f5f5',
                    color: '#999',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #A5B2BB',
                          color: '#999',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">2</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step2')}
                      </Typography>
                      <Typography variant="caption">{t('common.step2Title')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    flex: 1,
                    bgcolor: '#f5f5f5',
                    color: '#999',
                    p: 2,
                    borderRadius: 1,
                    border: '1px solid #A5B2BB',
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid size="auto">
                      <Grid
                        container
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          border: '1px solid #ccc',
                          color: '#999',
                          fontWeight: 'bold',
                        }}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid size="auto">2</Grid>
                      </Grid>
                    </Grid>
                    <Grid size="auto">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t('common.step3')}
                      </Typography>
                      <Typography variant="caption">{t('common.step3Title')}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>

            {/* Definir Objetivos Section */}

            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              {t('defineObjectives.title')}
            </Typography>
            <Box sx={{ p: 2, borderRadius: 1, border: '1px solid #A5B2BB' }}>
              {/* Focos Estratégicos */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('defineObjectives.strategicFocuses')}
                </Typography>

                {loadedFocuses.map((focus, index) => (
                  <Accordion
                    key={focus.APFocusId}
                    expanded={expandedPanel === focus.APFocusId}
                    onChange={handleAccordionChange(focus.APFocusId)}
                    sx={{
                      mb: 2,
                      boxShadow: 'none',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        bgcolor: '#F4F8F8',
                        borderRadius: 1,
                        border: '1px solid #A5B2BB',
                        minHeight: '40px !important',
                        height: '40px !important',
                        '& .MuiAccordionSummary-content': {
                          alignItems: 'center',
                          margin: '8px 0 !important',
                        },
                        '&.Mui-expanded': {
                          minHeight: '40px !important',
                          height: '40px !important',
                        },
                        '& .MuiAccordionSummary-content.Mui-expanded': {
                          margin: '8px 0 !important',
                        },
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {index + 1}. {focus.Name}
                      </Typography>
                    </AccordionSummary>
                    {expandedPanel === focus.APFocusId && (
                      <AccordionDetails sx={{ bgcolor: 'white', pt: 3, mx: -1.75 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                            {t('defineObjectives.enterObjectives')}
                          </Typography>

                          {/* Campo para nuevo objetivo */}
                          <NewObjective
                            focus={focus}
                            isAnyLoading={isAnyLoading}
                            handleAddObjective={handleAddObjective}
                            readOnlyMode={readOnlyMode}
                          />

                          {/* Lista de objetivos */}
                          {(objectivesByFocus[focus.APFocusId] ?? []).map((objective) => {
                            const isEditing = editingObjective?.id === objective.APObjectiveId;
                            return (
                              <Grid
                                key={objective.APObjectiveId}
                                container
                                alignItems="stretch"
                                spacing={1}
                                sx={{ mb: 2 }}
                              >
                                <Grid size={{ xs: 12, md: 'grow' }}>
                                  {isEditing ? (
                                    <TextField
                                      fullWidth
                                      value={editingValue}
                                      inputRef={editingInputRef}
                                      onChange={(event) =>
                                        handleEditingValueChange(event.target.value)
                                      }
                                      onPaste={(event) => {
                                        const pastedText =
                                          event.clipboardData?.getData('text') ?? '';
                                        const allowed = MAX_OBJECTIVE_LENGTH - editingValue.length;
                                        if (
                                          editingValue.length + pastedText.length >
                                          MAX_OBJECTIVE_LENGTH
                                        ) {
                                          event.preventDefault();
                                          const nextValue =
                                            editingValue +
                                            pastedText.slice(0, Math.max(allowed, 0));
                                          if (allowed > 0) {
                                            setEditingValue(nextValue);
                                          }
                                          setEditingError(
                                            t('defineObjectives.maxLengthExceeded', {
                                              max: MAX_OBJECTIVE_LENGTH.toString(),
                                            }),
                                          );
                                        }
                                      }}
                                      placeholder={t('defineObjectives.enterObjectiveName')}
                                      inputProps={{ maxLength: MAX_OBJECTIVE_LENGTH }}
                                      error={Boolean(editingError)}
                                      helperText={editingError ?? ' '}
                                      sx={{
                                        '& .MuiOutlinedInput-root': {
                                          bgcolor: 'white',
                                          '& fieldset': {
                                            borderColor: editingError ? '#d32f2f' : '#A5B2BB',
                                          },
                                        },
                                      }}
                                      size="small"
                                    />
                                  ) : (
                                    <Paper
                                      elevation={0}
                                      sx={{
                                        p: 1.5,
                                        minHeight: 40,
                                        bgcolor: '#F4F8F8',
                                        border: '1px solid #A5B2BB',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        height: '100%',
                                      }}
                                    >
                                      <Tooltip title={objective.Name} placement="top">
                                        <Typography
                                          variant="body2"
                                          sx={{ wordBreak: 'break-word' }}
                                        >
                                          {objective.Name}
                                        </Typography>
                                      </Tooltip>
                                    </Paper>
                                  )}
                                </Grid>
                                {!readOnlyMode && (
                                  <Grid
                                    size={{ xs: 12, md: 'auto' }}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: { xs: 'flex-end', md: 'flex-start' },
                                      gap: 1,
                                    }}
                                  >
                                    {isEditing ? (
                                      <>
                                        <IconButton
                                          size="small"
                                          onClick={handleConfirmEditObjective}
                                          disabled={isAnyLoading || Boolean(editingError)}
                                          sx={{
                                            border: '1px solid #A5B2BB',
                                            bgcolor: '#00BCE4',
                                            color: 'white',
                                            '&:hover': { bgcolor: '#009bbf' },
                                          }}
                                        >
                                          <Check fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={handleCancelEditObjective}
                                          disabled={isAnyLoading}
                                          sx={{
                                            border: '1px solid #A5B2BB',
                                            bgcolor: '#FFFFFF',
                                            color: '#5d6061ff',
                                          }}
                                        >
                                          <Close fontSize="small" />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleStartEditObjective(
                                              focus.APFocusId,
                                              objective.APObjectiveId,
                                            )
                                          }
                                          disabled={isAnyLoading}
                                          sx={{
                                            bgcolor: '#1976d2',
                                            color: '#fff',
                                            borderRadius: '8px',
                                            width: 32,
                                            height: 32,
                                            p: 0,
                                            '&:hover': {
                                              bgcolor: '#1565c0',
                                            },
                                          }}
                                        >
                                          <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => handleRequestDeleteObjective(objective)}
                                          disabled={isAnyLoading}
                                          sx={{
                                            bgcolor: '#f44336',
                                            color: '#fff',
                                            borderRadius: '8px',
                                            width: 32,
                                            height: 32,
                                            p: 0,
                                            '&:hover': {
                                              bgcolor: '#d32f2f',
                                            },
                                          }}
                                        >
                                          <Close fontSize="small" />
                                        </IconButton>
                                      </>
                                    )}
                                  </Grid>
                                )}
                              </Grid>
                            );
                          })}
                        </Box>
                      </AccordionDetails>
                    )}
                  </Accordion>
                ))}
              </Box>
            </Box>

            {/* Buttons */}
            <Navigation
              onSaveDraft={handleSaveProgress}
              saveDraftTitle={t('defineObjectives.saveDraft')}
              readOnlyMode={readOnlyMode}
              onNext={handleContinue}
              onExit={handleExit}
              onBack={handleBack}
              backTitle={readOnlyMode ? t('common.viewDetails') : t('common.editAnnualPlan')}
              exitTitle={t('common.exit')}
              isAnyLoading={isAnyLoading}
              disableNext={disableNext}
              disableReason={disableNextReason}
              nextTitle={
                readOnlyMode ? t('common.continue') : t('defineObjectives.saveAndContinue')
              }
            />
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={Boolean(objectivePendingDeletion)}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            boxShadow: 4,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 0, pt: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
            {t('defineObjectives.deleteDialogTitle')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Warning sx={{ fontSize: 64, color: '#29b6f6', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
              {t('defineObjectives.deleteDialogMessage', {
                name: objectivePendingDeletion?.Name ?? '',
              })}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, maxWidth: '80%' }}>
              {deleteActivitiesMessage}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 2, lineHeight: 1.6 }}>
              {t('defineObjectives.deleteDialogIrreversible')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 4, gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            disabled={isAnyLoading}
            onClick={handleCloseDeleteDialog}
            sx={{
              borderColor: '#00BCE4',
              color: '#00BCE4',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                borderColor: '#00acc1',
                bgcolor: 'rgba(0, 188, 212, 0.04)',
              },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            size="large"
            disabled={isAnyLoading}
            onClick={handleConfirmDeleteObjective}
            sx={{
              bgcolor: '#f44336',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 20,
              textTransform: 'none',
              minWidth: 150,
              '&:hover': { bgcolor: '#d32f2f' },
            }}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DefineObjectives;
